import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { Company, Deal, Person, Route } from "@mapmycustomers/shared/types/entity";
import { PlatformFilterCondition } from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import PeopleAssociation from "@app/component/associations/PeopleAssociation";
import { RootState } from "@app/store/rootReducer";

import Relationships from "../../components/Relationships";
import { COMPANY_RELATIONSHIP_TYPES } from "../../enum/RelationshipType";
import { updateCompanyPrimaryPerson } from "../store/actions";
import {
  addCompanyChildCompanies,
  addCompanyDeals,
  addCompanyParentCompany,
  addCompanyPeople,
  fetchCompanyRoutes,
  fetchMoreDeals,
  fetchMorePeople,
  removeCompanyChildCompany,
  removeCompanyDeal,
  removeCompanyFromRoute,
  removeCompanyParentCompany,
  removeCompanyPerson,
} from "../store/actions";
import {
  getRemoveCompanyRouteLoadingsIds,
  isAddCompanyChildCompaniesLoading,
  isAddCompanyDealsLoading,
  isAddCompanyParentCompanyLoading,
  isAddCompanyPersonsLoading,
  isCompanyRoutesLoading,
} from "../store/selectors";

interface Props {
  childCompanies?: Company[];
  company: Company;
  deals: Deal[];
  parentCompany?: Company;
  people: Person[];
  routes: Route[];
  totalDeals: number;
  totalPeople: number;
}

const RelationshipsContainer: React.FC<
  Props & typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>
> = ({
  addCompanyChildCompanies,
  addCompanyChildCompaniesLoading,
  addCompanyDeals,
  addCompanyDealsLoading,
  addCompanyParentCompany,
  addCompanyParentCompanyLoading,
  addCompanyPeople,
  addCompanyPersonsLoading,
  childCompanies,
  company,
  companyRoutesLoading,
  deals,
  fetchCompanyRoutes,
  fetchMoreDeals,
  fetchMorePeople,
  parentCompany,
  people,
  removeCompanyChildCompany,
  removeCompanyDeal,
  removeCompanyFromRoute,
  removeCompanyParentCompany,
  removeCompanyPerson,
  removeCompanyRouteLoadingIds,
  routes,
  totalDeals,
  totalPeople,
  updateCompanyPrimaryPerson,
}) => {
  const intl = useIntl();

  const [modalVisible, onShow, onHide] = useBoolean();

  const [primaryPersonId, setPrimaryPersonId] = useState<Person["id"] | undefined>(
    company.primaryContactId ?? undefined
  );

  const requestFilters: PlatformFilterCondition[] = useMemo(
    () => [
      {
        $and: [
          {
            id: { $in: people.map(({ id }) => id) },
          },
        ],
      },
    ],
    [people]
  );

  const handleAddToRoute = useCallback(
    () => fetchCompanyRoutes(company.id),
    [company.id, fetchCompanyRoutes]
  );

  const handleRemoveParentCompany = useCallback(
    () => removeCompanyParentCompany(company.id),
    [company.id, removeCompanyParentCompany]
  );

  const handleSelectPrimaryPerson = useCallback(
    (primaryPersonIds: Person["id"][]) => {
      if (primaryPersonIds.length) {
        updateCompanyPrimaryPerson({
          id: company.id,
          primaryPersonId: primaryPersonIds[0],
        });
      }
    },
    [company.id, updateCompanyPrimaryPerson]
  );

  const handleUpdatePrimaryPerson = useCallback(
    (primaryPersonId?: Person["id"]) => setPrimaryPersonId(primaryPersonId),
    []
  );

  const handleUnsetPrimaryPerson = useCallback(() => {
    updateCompanyPrimaryPerson({
      id: company.id,
      primaryPersonId: null,
    });
  }, [company.id, updateCompanyPrimaryPerson]);

  const getListTitle = useCallback(
    (count: number, total: number) =>
      intl.formatMessage(
        {
          id: "preview.personRecordPane.relationshipsContainer.primaryCompanyModal.subtitle",
          defaultMessage: "Associated Companies ({count}/{total})",
          description: "Subtitle of a Select Primary Company modal in the Preview Record pane",
        },
        { count, total }
      ),
    [intl]
  );

  useEffect(() => {
    setPrimaryPersonId(company.primaryContactId ?? undefined);
  }, [company.primaryContactId]);

  return (
    <>
      <Relationships
        addChildCompaniesLoading={addCompanyChildCompaniesLoading}
        addDealsLoading={addCompanyDealsLoading}
        addParentCompanyLoading={addCompanyParentCompanyLoading}
        addPersonsLoading={addCompanyPersonsLoading}
        addRoutesLoading={companyRoutesLoading}
        childCompanies={childCompanies}
        deals={deals}
        entity={company}
        onAddToRoute={handleAddToRoute}
        onChangePrimaryPerson={onShow}
        onFetchMoreDeals={fetchMoreDeals}
        onFetchMorePeople={fetchMorePeople}
        onRemoveChildCompany={removeCompanyChildCompany}
        onRemoveDeal={removeCompanyDeal}
        onRemoveParentCompany={handleRemoveParentCompany}
        onRemovePeople={removeCompanyPerson}
        onRemoveRoute={removeCompanyFromRoute}
        onSelectChildCompanies={addCompanyChildCompanies}
        onSelectDeals={addCompanyDeals}
        onSelectParentCompany={addCompanyParentCompany}
        onSelectPeople={addCompanyPeople}
        onUnsetPrimaryPerson={company.primaryContact ? handleUnsetPrimaryPerson : undefined}
        onUpdatePrimaryPerson={handleUpdatePrimaryPerson}
        parentCompany={parentCompany}
        people={people}
        primaryPersonId={primaryPersonId}
        removeRouteLoadingIds={removeCompanyRouteLoadingIds}
        routes={routes}
        supportedRelationshipTypes={COMPANY_RELATIONSHIP_TYPES}
        totalDeals={totalDeals}
        totalPeople={totalPeople}
      />
      {modalVisible && (
        <PeopleAssociation
          allowAdd={false}
          assignedPeople={company.primaryContact ? [company.primaryContact] : undefined}
          associateWith={company.id}
          getListTitle={getListTitle}
          multiselect={false}
          onHide={onHide}
          onSelect={handleSelectPrimaryPerson}
          requestFilters={requestFilters}
          title={intl.formatMessage(
            {
              id: "preview.companyRecordPane.relationshipsContainer.primaryPersonModal.title",
              defaultMessage: "Set Primary Person for {companyName}",
              description: "Title of a Select Primary Person modal in the Preview Record pane",
            },
            { companyName: company.name }
          )}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  addCompanyChildCompaniesLoading: isAddCompanyChildCompaniesLoading(state),
  addCompanyDealsLoading: isAddCompanyDealsLoading(state),
  addCompanyParentCompanyLoading: isAddCompanyParentCompanyLoading(state),
  addCompanyPersonsLoading: isAddCompanyPersonsLoading(state),
  companyRoutesLoading: isCompanyRoutesLoading(state),
  removeCompanyRouteLoadingIds: getRemoveCompanyRouteLoadingsIds(state),
});

const mapDispatchToProps = {
  addCompanyChildCompanies: addCompanyChildCompanies.request,
  addCompanyDeals: addCompanyDeals.request,
  addCompanyParentCompany: addCompanyParentCompany.request,
  addCompanyPeople: addCompanyPeople.request,
  fetchCompanyRoutes: fetchCompanyRoutes.request,
  fetchMoreDeals: fetchMoreDeals.request,
  fetchMorePeople: fetchMorePeople.request,
  removeCompanyChildCompany: removeCompanyChildCompany.request,
  removeCompanyDeal: removeCompanyDeal.request,
  removeCompanyFromRoute: removeCompanyFromRoute.request,
  removeCompanyParentCompany: removeCompanyParentCompany.request,
  removeCompanyPerson: removeCompanyPerson.request,
  updateCompanyPrimaryPerson: updateCompanyPrimaryPerson.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(RelationshipsContainer);
